import clsx from 'clsx';
import { Icon } from 'components/Icon';
import { NUMBER_PICKER_TEST_ID } from 'components/NumberPicker/const';
import { FC, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import { useMomentary } from 'hooks/useMomentary/useMomentary';
import { useCounter } from 'hooks/useCounter/useCounter';
import { useBowser } from 'hooks/useBowser/useBowser';

export type NumberPickerProps = {
  value: number;
  onChange: (val: number) => void;
  className?: string;
  children?: never;
  min?: number;
  max?: number;
}

// Increment/Decrement Speed in ms
const INTERVAL = 160;
const MOBILE_MULTIPLIER = 1.5;

export const NumberPicker: FC<NumberPickerProps> = memo(function NumberPicker({
  value, onChange, min, max, className, ...props
}) {
  if (min !== undefined && max !== undefined && min > max) {
    throw new Error(`Min of ${min} is greater than the max of ${max}`);
  }

  const { t } = useTranslation();
  const [counter, increment, decrement, setValue] = useCounter(value, min, max, 1);
  const input = useRef<HTMLInputElement>(null);
  const browser = useBowser();
  const interval = useMemo(() => INTERVAL * (browser.is('mobile') ? MOBILE_MULTIPLIER : 1), [browser]);
  const [onIncrementPress, onIncrementRelease, onIncrementClick] = useMomentary(increment, interval);
  const [onDecrementPress, onDecrementRelease, onDecrementClick] = useMomentary(decrement, interval);
  const onInputBlur = useCallback(() => {
    if (input.current) {
      input.current.valueAsNumber = counter.value;
    }
  }, [counter]);

  // force release when min/max hit
  useEffect(() => { if (counter.isAtMin) onDecrementRelease(); }, [counter, onDecrementRelease]);
  useEffect(() => { if (counter.isAtMax) onIncrementRelease(); }, [counter, onIncrementRelease]);

  useEffect(() => {
    if (input.current) {
      input.current.valueAsNumber = counter.value;
    }
    if (value !== counter.value) onChange(counter.value);
  }, [value, counter.value, onChange]);

  return (
    <div className={clsx(styles.numberPicker, className)} {...props}>
      <button
        type="button"
        disabled={counter.isAtMin}
        aria-label={t('decreaseQuantity')}
        onClick={onDecrementClick}
        onBlur={onDecrementRelease}
        onMouseOut={onDecrementRelease}
        onMouseDownCapture={onDecrementPress}
        onMouseUpCapture={onDecrementRelease}
        onTouchStartCapture={onDecrementPress}
        onTouchEndCapture={onDecrementRelease}
        onTouchCancelCapture={onDecrementRelease}
        data-testid={NUMBER_PICKER_TEST_ID.decrement}
      >
        {!counter.isAtMin ? <Icon name="subtract" size={15} /> : <>&nbsp;</>} {/* Need the &nbsp; so it is not empty and we can do height: 100% */}
      </button>
      <input
        ref={input}
        aria-label={t('quantity')}
        size={5} // browser seemed to be ensuring the default value of at least 20 characters was visible
        step={counter.step} // only whole numbers
        min={counter.min}
        max={counter.max}
        type="number"
        pattern="^[0-9\-]+$"
        defaultValue={counter.value}
        onChange={setValue}
        onBlur={onInputBlur}
        data-testid={NUMBER_PICKER_TEST_ID.value}
      />
      <button
        type="button"
        disabled={counter.isAtMax}
        aria-label={t('increaseQuantity')}
        onClick={onIncrementClick}
        onBlur={onIncrementRelease}
        onMouseOut={onIncrementRelease}
        onMouseDownCapture={onIncrementPress}
        onMouseUpCapture={onIncrementRelease}
        onTouchStartCapture={onIncrementPress}
        onTouchEndCapture={onIncrementRelease}
        onTouchCancelCapture={onIncrementRelease}
        data-testid={NUMBER_PICKER_TEST_ID.increment}
      >
        {!counter.isAtMax ? <Icon name="add" size={15} /> : <>&nbsp;</>}
      </button>
    </div>
  );
});
